import { ReactElement } from 'react'
import { Typography } from '@mui/material'
import { Message, MessageSender } from '@weblite/ts-action/message'
import { useUser } from '$logic/users'
import { useHall } from '$logic/hall/react-hook'
import { useWapp } from '$logic/wapps/react-hook'
import { t } from '$i18n'
import { getWappTheme } from '$common/wapp-bar-theme'

function SenderNameTypography({
  children: body,
  fromMe,
}: {
  children: string
  fromMe: boolean
}) {
  return (
    <Typography
      variant="subtitle4"
      color={fromMe ? 'secondary100' : 'secondary500'}
      sx={{ display: 'block', lineHeight: '14px' }}
      noWrap
    >
      {body}
    </Typography>
  )
}

interface SenderNameComponentProps {
  id: string
  fromMe: boolean
}

function WappName({ id }: SenderNameComponentProps) {
  useWapp(id)
  const wappTheme = getWappTheme(id)
  return (
    <SenderNameTypography>
      {`${t('symbols.closingDoubleQuotation')}${wappTheme.name}${t(
        'symbols.openingDoubleQuotation',
      )}`}
    </SenderNameTypography>
  )
}

function ChannelName({ id, fromMe }: SenderNameComponentProps) {
  const { entity: hall } = useHall(id)
  return (
    <SenderNameTypography fromMe={fromMe}>{hall?.name || ''}</SenderNameTypography>
  )
}

function UserName({ id, fromMe }: SenderNameComponentProps) {
  const { user } = useUser(id)
  return (
    <SenderNameTypography fromMe={fromMe}>
      {user?.firstname || ''}
    </SenderNameTypography>
  )
}

const senderNameComponentMap: Record<
  string,
  ((props: SenderNameComponentProps) => ReactElement) | undefined
> = {
  SYSTEM: undefined,
  USER: UserName,
  CHANNEL: ChannelName,
  WAPP: WappName,
}

export function SenderName({
  message,
  fromMe,
}: {
  message: Message
  fromMe: boolean
}) {
  if (message.patches?.deleted || message.type === 'EVENT') return null

  // Should consider USER, CHANNEL and WAPP; so senderType cannot be SYSTEM anymore.
  const senderType = message.sender.type
  const senderId = (message.sender as MessageSender).id
  const SenderNameComponent = senderNameComponentMap[senderType]

  if (!SenderNameComponent) return null
  return <SenderNameComponent id={senderId} fromMe={fromMe} />
}
